/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { OperationalFilterSearchField } from '../../shared/filters/OperationalFilterPrimitives'
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { useOperationalWebSearchFocus } from '../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { formatMoneyCompact } from '../../shared/format/moneyFormat';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet'
import type { TicketMoreAction } from '../../shared/ticket/TicketMoreActionsSheet';
import type { PurchasesSummary } from '../domain/purchaseTypes';
import {
  formatPurchaseQueueChipLabel,
  PURCHASE_QUEUE_IDS,
  type PurchaseQueueFilter,
} from '../domain/purchaseQueues';

type Props = {
  summary: PurchasesSummary;
  search: string;
  onSearchChange: (v: string) => void;
  queueFilter: PurchaseQueueFilter;
  onQueueFilterChange: (v: PurchaseQueueFilter) => void;
  onNewOrder: () => void;
  onBuyNow: () => void;
  onOpenSupplierAccount: () => void;
  onTransfer: () => void;
  canEdit: boolean;
  /** Multi-sucursal: oculta 'Transferir stock' del menú Más. */
  canTransfer?: boolean;
  filterTrigger?: React.ReactNode;
  /** Cero órdenes sin filtros: oculta KPIs y chips de cola (ruido en vacío). */
  hideMetrics?: boolean;
};

export function PurchasesScreenHeader({
  summary,
  search,
  onSearchChange,
  queueFilter,
  onQueueFilterChange,
  onNewOrder,
  onBuyNow,
  onOpenSupplierAccount,
  onTransfer,
  canEdit,
  canTransfer = true,
  filterTrigger,
  hideMetrics = false,
}: Props) {
  const { layoutWide } = useOperationalFilterLayout();
  const searchInputRef = useRef<TextInput>(null);
  useOperationalWebSearchFocus(searchInputRef, { shortcutsEnabled: true });

  const queueCounts: Record<(typeof PURCHASE_QUEUE_IDS)[number], number> = {
    to_receive: summary.pendingReception,
    to_pay: summary.toPay,
    overdue: summary.overdue,
  };

  const toggleQueue = (queue: (typeof PURCHASE_QUEUE_IDS)[number]) =>
    onQueueFilterChange(queueFilter === queue ? 'all' : queue);

  // Desktop: 5 tiles (proto); mobile: solo las 3 colas accionables.
  const kpiItemsDesk = useMemo<TicketKpiItem[]>(
    () => [
      { label: 'Órdenes', value: String(summary.totalOrders) },
      { label: 'Monto total', value: formatMoneyCompact(summary.totalAmount) },
      {
        label: 'Por recibir',
        value: String(summary.pendingReception),
        tone: summary.pendingReception > 0 ? 'warn' : 'default',
        onPress: () => toggleQueue('to_receive'),
        selected: queueFilter === 'to_receive',
        testID: 'purchases.kpi.to-receive',
      },
      {
        label: 'Por pagar',
        value: formatMoneyCompact(summary.toPayAmount),
        tone: summary.toPay > 0 ? 'warn' : 'default',
        onPress: () => toggleQueue('to_pay'),
        selected: queueFilter === 'to_pay',
        testID: 'purchases.kpi.to-pay',
      },
      {
        label: 'Vencidas',
        value: String(summary.overdue),
        tone: summary.overdue > 0 ? 'bad' : 'default',
        onPress: () => toggleQueue('overdue'),
        selected: queueFilter === 'overdue',
        testID: 'purchases.kpi.overdue',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, queueFilter],
  );

  const kpiItemsMobile = useMemo<TicketKpiItem[]>(
    () => kpiItemsDesk.slice(2),
    [kpiItemsDesk],
  );

  const queueChips = (
    <View style={styles.queueRow} testID="purchases-queue-chips">
      {PURCHASE_QUEUE_IDS.map((queue) => {
        const active = queueFilter === queue;
        const label = formatPurchaseQueueChipLabel(queue, queueCounts[queue]);
        const warnChip = !active && queue === 'overdue' && queueCounts.overdue > 0;
        return (
          <TouchableOpacity
            key={queue}
            style={[styles.queueChip, active && styles.queueChipActive, warnChip && styles.queueChipWarn]}
            onPress={() => onQueueFilterChange(queue)}
            accessibilityRole="button"
            accessibilityLabel={label}
            testID={`purchases-queue-${queue}`}
          >
            <Text style={[styles.queueChipText, active && styles.queueChipTextActive, warnChip && styles.queueChipTextWarn]}>
              {label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const moreActions: TicketMoreAction[] = [
    {
      key: 'account',
      label: 'Cuenta corriente',
      hint: 'Saldo con tus proveedores',
      icon: 'receipt-outline',
      onPress: onOpenSupplierAccount,
      disabled: false,
      testID: 'purchases.account',
    },
    ...(canEdit
      ? [
          {
            key: 'buyNow',
            label: 'Compré mercadería',
            hint: 'Registrá una compra sin orden previa',
            icon: 'cart-outline' as const,
            onPress: onBuyNow,
            disabled: false,
            testID: 'purchases.buy-now',
          },
          ...(canTransfer
            ? [
                {
                  key: 'transfer',
                  label: 'Transferir stock',
                  hint: 'Mover mercadería entre sucursales',
                  icon: 'swap-horizontal-outline' as const,
                  onPress: onTransfer,
                  disabled: false,
                  testID: 'purchases.transfer',
                },
              ]
            : []),
        ]
      : []),
  ];

  const [moreActionsSheetOpen, setMoreActionsSheetOpen] = React.useState(false);

  const moreActionsSheet = (
    <TicketMoreActionsSheet
      visible={moreActionsSheetOpen}
      title="Acciones de compras"
      actions={moreActions}
      onClose={() => setMoreActionsSheetOpen(false)}
    />
  );

  const primaryButton = (
    <TouchableOpacity
      style={[styles.deskBtnGold, !canEdit && posUi.disabledCard]}
      onPress={onBuyNow}
      disabled={!canEdit}
      accessibilityRole="button"
      accessibilityLabel="Compré mercadería"
      testID="purchases.buy-now"
    >
      <Text style={styles.deskBtnGoldText}>Compré mercadería</Text>
    </TouchableOpacity>
  );

  // ── Wide: TicketModuleHeader con search + queue chips + filtros + Más ──
  if (layoutWide) {
    return (
      <View style={styles.headerStack}>
        <TicketModuleHeader
          kicker="Compras"
          title="Órdenes de compra"
          meta="Pedí, recibí y pagá mercadería"
          testID="purchases.header.sheet"
          trailing={canEdit ? primaryButton : null}
          tools={
            <View style={styles.deskToolsRow}>
              <View style={styles.deskSearchFlex}>
                <OperationalFilterSearchField
                  inputRef={searchInputRef}
                  value={search}
                  onChangeText={onSearchChange}
                  placeholder="Buscar órdenes…"
                  appearance="desk"
                  testID="purchases-search"
                  accessibilityLabel="Buscar órdenes"
                />
              </View>
              {filterTrigger}
              {/* Tesorería (Cuenta corriente / Transferir) vive en el sheet Más,
                  fuera de la fila de acciones de compra. */}
              {canEdit ? (
                <TouchableOpacity
                  style={styles.deskBtn}
                  onPress={onNewOrder}
                  testID="purchases.new-order"
                  accessibilityRole="button"
                  accessibilityLabel="Orden de compra"
                  accessibilityHint="Armá un pedido antes de recibir"
                >
                  <Text style={styles.deskBtnText}>Orden de compra</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity
                style={styles.deskBtn}
                onPress={() => setMoreActionsSheetOpen(true)}
                accessibilityRole="button"
                accessibilityLabel="Más acciones de compras"
                testID="purchases.more.open"
              >
                <Text style={styles.deskBtnText}>Más</Text>
              </TouchableOpacity>
            </View>
          }
        >
          {moreActionsSheet}
        </TicketModuleHeader>

        {!hideMetrics ? queueChips : null}

        {!hideMetrics ? (
          <PaperSheet testID="purchases.kpi.sheet">
            <TicketKpiStrip items={kpiItemsDesk} layout="desk" variant="soft" />
          </PaperSheet>
        ) : null}
      </View>
    );
  }

  // ── Mobile: TicketModuleHeader con search + queue chips + botones ──
  return (
    <View style={styles.headerStack}>
      <TicketModuleHeader
        kicker="Compras"
        title="Órdenes de compra"
        meta="Pedí, recibí y pagá mercadería"
        testID="purchases.header.sheet"
        tools={
          <>
            <View style={styles.searchRowMobile}>
              <View style={styles.searchBoxMobile}>
                <Ionicons name="search-outline" size={18} color={ticketColors.ink3} />
                <TextInput
                  ref={searchInputRef}
                  value={search}
                  onChangeText={onSearchChange}
                  placeholder="Buscar órdenes…"
                  placeholderTextColor={ticketColors.ink3}
                  style={styles.searchInputMobile}
                  accessibilityLabel="Buscar órdenes"
                  autoCorrect={false}
                  returnKeyType="search"
                  testID="purchases-search"
                />
                {search.trim().length > 0 ? (
                  <TouchableOpacity
                    onPress={() => onSearchChange('')}
                    accessibilityRole="button"
                    accessibilityLabel="Limpiar búsqueda"
                  >
                    <Ionicons name="close-circle" size={20} color={ticketColors.ink2} />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>

            {!hideMetrics ? queueChips : null}

            {canEdit ? (
              <TouchableOpacity
                style={[styles.actionPrimaryMobile, !canEdit && posUi.disabledCard]}
                onPress={onBuyNow}
                disabled={!canEdit}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Compré mercadería"
                testID="purchases.buy-now"
              >
                <Text style={styles.actionPrimaryTextMobile}>Compré mercadería</Text>
              </TouchableOpacity>
            ) : null}

            <View style={styles.toolsRowMobile}>
              {filterTrigger}
              {canEdit ? (
                <TouchableOpacity
                  style={styles.actionMoreMobile}
                  onPress={onNewOrder}
                  accessibilityRole="button"
                  accessibilityLabel="Orden de compra"
                  testID="purchases.new-order"
                  activeOpacity={0.6}
                >
                  <Text style={styles.actionMoreTextMobile}>Orden de compra</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity
                style={styles.actionMoreMobile}
                onPress={() => setMoreActionsSheetOpen(true)}
                accessibilityRole="button"
                accessibilityLabel="Más acciones de compras"
                accessibilityHint="Cuenta corriente y transferir stock"
                testID="purchases.more.open"
                activeOpacity={0.6}
              >
                <Text style={styles.actionMoreTextMobile}>Más</Text>
              </TouchableOpacity>
            </View>
          </>
        }
      >
        {moreActionsSheet}
      </TicketModuleHeader>

      {!hideMetrics ? (
        <PaperSheet testID="purchases.kpi.sheet">
          <TicketKpiStrip items={kpiItemsMobile} layout="mobile" variant="soft" />
        </PaperSheet>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  headerStack: {
    gap: 10,
    paddingBottom: spacing.sm,
  },
  deskToolsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  deskSearchFlex: {
    flex: 1,
    minWidth: 180,
  },
  deskBtn: {
    height: 42,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deskBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  deskBtnGold: {
    height: 42,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: ticketColors.brandPrimary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deskBtnGoldText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#FFFFFF',
    fontFamily: ticketFonts.sans,
  },
  queueRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  queueChip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  queueChipActive: {
    backgroundColor: ticketColors.brandPrimary,
    borderColor: ticketColors.brandPrimary,
  },
  queueChipWarn: {
    backgroundColor: ticketColors.warnBg,
    borderColor: ticketColors.warnBorder,
  },
  queueChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  queueChipTextActive: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  queueChipTextWarn: {
    color: ticketColors.warn,
  },
  searchRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchBoxMobile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 12,
    height: 42,
  },
  searchInputMobile: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    paddingVertical: 0,
  },
  toolsRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionPrimaryMobile: {
    height: 42,
    borderRadius: 10,
    backgroundColor: ticketColors.brandPrimary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionPrimaryTextMobile: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  actionMoreMobile: {
    flex: 1,
    height: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionMoreTextMobile: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
});
